import { KeyboardArrowDown, KeyboardDoubleArrowDown, Refresh } from '@mui/icons-material'
import {
  alpha,
  Box,
  Divider,
  IconButton,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_TableInstance,
  useMaterialReactTable,
} from 'material-react-table'
import { MRT_Localization_ES } from 'material-react-table/locales/es'
import React, { useCallback, useMemo } from 'react'

import { alphaByTheme, alphaNormal } from '../../../utils/colorUtils.ts'
import { MuiToolbarAlertBannerProps } from '../../../utils/muiTable/materialReactTableUtils.ts'
import { ActionIconButton } from './ActionIconButton.tsx'
import { MrtFlatTable } from './MrtFlatTable.tsx'
import { MrtAuditPopoverAction } from './MrtIconButtonAuditoria.tsx'
import { MrtMobileListView } from './MrtMobileListView.tsx'
import { MrtRowMenu } from './MrtRowMenu.tsx'
import {
  MrtNativeRenderRowActionsParams,
  MrtPaginatedResponse,
  MrtQueryFnResult,
  MrtRowActionsContentParams,
  MrtStateChangeHandlers,
  MrtSyncedState,
  MrtTableConfig,
} from './mrtTypes.ts'

interface Props<T extends Record<string, any>> {
  // Propiedades de configuracion
  config: MrtTableConfig<T>
  // Datos (puede ser Array o Objeto Paginado)
  data?: MrtQueryFnResult<T>
  isLoading?: boolean
  isFetching?: boolean
  isError?: boolean
  refetch?: () => Promise<any>
  // Sincronización de Estado (Opcional, para modo Servidor)
  state?: MrtSyncedState
  // Setters de Estado (Opcional, para modo Servidor)
  onStateChange?: MrtStateChangeHandlers
}

const mrtRowExpandColor = (theme: Theme, props: { dark?: number; light?: number } = {}) =>
  alphaByTheme(theme.palette.primary.main, theme, props.dark ?? 0.3, props.light ?? 0.5)

// Evita recrear la función por defecto en cada render (previene Memory Leaks por closures)
const defaultRefetch = async () => {}

/**
 * Componente de tabla dinámica completa reutilizable
 * @param config
 * @param externalData
 * @param onStateChange
 * @author isi-template
 * @constructor
 */
export const MrtDynamicTable = <T extends Record<string, any>>({
  config,
  data,
  isLoading = false,
  isFetching = false,
  isError = false,
  refetch = defaultRefetch,
  state,
  onStateChange,
}: Props<T>) => {
  // Detección de vista móvil
  const theme = useTheme()
  const mobileConfig = config.mobileConfig
  const mobileBreakpoint = mobileConfig?.breakpoint ?? 'sm'
  const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint))
  const isMobileMode = isMobile && mobileConfig?.enabled !== false && !!mobileConfig

  // 1. NORMALIZACIÓN INTELIGENTE DE DATOS
  const { tableData, totalCount, isServerSideData } = useMemo(() => {
    if (!data) return { tableData: [] as T[], totalCount: 0, isServerSideData: false }

    if ('docs' in (data as any)) {
      const p = data as MrtPaginatedResponse<T>
      return {
        tableData: p.docs || [],
        totalCount: p.pageInfo?.totalDocs ?? 0,
        isServerSideData: true,
      }
    }

    if (Array.isArray(data)) {
      return {
        tableData: data,
        totalCount: data.length,
        isServerSideData: false,
      }
    }

    return { tableData: [] as T[], totalCount: 0, isServerSideData: false }
  }, [data])

  // 2. DETECCIÓN DE MODO
  const hasStateHandlers =
    !!onStateChange?.onPaginationChange ||
    !!onStateChange?.onSortingChange ||
    !!onStateChange?.onColumnFiltersChange ||
    !!onStateChange?.onGlobalFilterChange

  const isManual = config.manualPagination ?? isServerSideData
  const isFullWidth = config.fullWidth ?? true

  const customRenderRowActions = config.additionalOptions?.renderRowActions

  const {
    state: _omittedState,
    onRowSelectionChange: _omittedOnSelection,
    renderRowActions: _omittedRenderRowActions,
    ...cleanAdditionalOptions
  } = config.additionalOptions || {}

  // Solo tiene sentido calcular/renderizar la columna de acciones en modo desktop:
  // en móvil, MrtMobileListView dibuja sus propias acciones por tarjeta.
  const hasRowActions =
    !!config.rowActions ||
    !!config.rowIconsActions ||
    !!config.showAudit ||
    !!config.rowMenuActions ||
    !!customRenderRowActions
  const enableRowActions = !isMobileMode && hasRowActions
  const hasDetailPanel = !!config.renderDetailPanel
  const enableExpanding = !isMobileMode && hasDetailPanel

  // Memorizar el ancho de las acciones para no calcularlo en cada ciclo del render
  const actionColumnSize = useMemo(() => {
    const iconWidth = (config.rowIconsActions?.length || 0) * 32
    const auditWidth = config.showAudit ? 32 : 0
    const menuWidth = config.rowMenuActions ? 32 : 0
    const customActionsWidth = Array.isArray(config.rowActions)
      ? config.rowActions.reduce((acc, curr) => acc + (curr.width || 90), 0)
      : 0
    return iconWidth + auditWidth + menuWidth + customActionsWidth + 10
  }, [config.rowIconsActions, config.showAudit, config.rowMenuActions, config.rowActions])

  // Contenido "propio" de acciones (iconos, menú, rowActions custom, auditoría).
  // Solo depende de row.original -> mismo código sirve para desktop y mobile,
  // sin necesitar un MRT_Row real ni castear nada.
  const renderRowActionsContent = useCallback(
    ({ row }: MrtRowActionsContentParams<T>) => (
      <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
        {config.rowMenuActions && (
          <MrtRowMenu row={row.original} actions={config.rowMenuActions} refetch={refetch} />
        )}
        {config.rowIconsActions?.map((action, i) => {
          const isHidden = action.hidden ? action.hidden(row.original) : false
          if (isHidden) return null
          return (
            <ActionIconButton
              key={i}
              row={row.original}
              refetch={refetch}
              {...action}
              disabled={action.enabled ? !action.enabled(row.original) : false}
              loading={action.loading ? action.loading(row.original) : false}
            />
          )
        })}
        {config.rowActions?.map((action, index) => (
          <Box key={`${action.id}-${index}`} id={`${action.id}-${index}`}>
            {action.render({ row: row.original, refetch })}
          </Box>
        ))}
        {config.showAudit && <MrtAuditPopoverAction data={row.original as any} />}
      </Box>
    ),
    [config.rowMenuActions, config.rowIconsActions, config.rowActions, config.showAudit, refetch],
  )

  // Este SÍ es el renderRowActions nativo que invoca MaterialReactTable en modo
  // desktop: recibe cell/row/table/staticRowIndex reales, así que aquí podemos
  // pasarle esos mismos parámetros al override del usuario (config.additionalOptions
  // .renderRowActions) sin ningún cast, porque coinciden 1:1 con lo que MRT espera.
  const renderRowActions = useCallback(
    (nativeParams: MrtNativeRenderRowActionsParams<T>) => (
      <>
        {customRenderRowActions?.(nativeParams)}
        {renderRowActionsContent({ row: nativeParams.row, table: nativeParams.table })}
      </>
    ),
    [customRenderRowActions, renderRowActionsContent],
  )

  // Cachea el contenido del panel de detalle UNA vez por cambio de tableData,
  // en vez de invocar config.renderDetailPanel dos veces por fila y por render
  // (una en getRowCanExpand, otra en renderDetailPanel).
  const detailPanelCache = useMemo(() => {
    if (!hasDetailPanel) return null
    const cache = new Map<T, React.ReactNode>()
    for (const row of tableData) {
      cache.set(row, config.renderDetailPanel!(row))
    }
    return cache
  }, [hasDetailPanel, tableData, config.renderDetailPanel])

  const getRowCanExpand = useCallback(
    (row: { original: T }) => {
      if (!detailPanelCache) return false
      const content = detailPanelCache.get(row.original)
      return content !== null && content !== undefined
    },
    [detailPanelCache],
  )

  const renderDetailPanel = useMemo(
    () =>
      hasDetailPanel ? ({ row }: { row: { original: T } }) => detailPanelCache?.get(row.original) : undefined,
    [hasDetailPanel, detailPanelCache],
  )

  // displayColumnDefOptions memorizado: evita recrear los componentes Header/Cell
  // de las columnas de expansión y acciones en cada render.
  const displayColumnDefOptions = useMemo<MrtTableConfigDisplayColumnDefOptions<T>>(
    () => ({
      'mrt-row-expand': {
        size: 45,
        muiTableHeadCellProps: {
          sx: {
            padding: 0,
            mt: -1,
            position: 'relative',
            '& .Mui-TableHeadCell-Content': { height: '100%', width: '100%' },
            '& .Mui-TableHeadCell-Content-Wrapper': { flex: 1, height: '100%', width: '100%' },
            '& .Mui-TableHeadCell-Content-Labels': { flex: 1, height: '100%' },
          },
        },
        Header: ({ table }) => {
          if (!enableExpanding) return null
          const allExpanded = table.getIsAllRowsExpanded()
          return (
            <Box
              onClick={table.getToggleAllRowsExpandedHandler()}
              sx={(theme) => ({
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                '&:hover .expand-icon-wrapper': {
                  borderColor: mrtRowExpandColor(theme),
                  backgroundColor: allExpanded
                    ? mrtRowExpandColor(theme)
                    : mrtRowExpandColor(theme, { dark: 0.2, light: 0.5 }),
                },
              })}
            >
              <Box
                className="expand-icon-wrapper"
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: allExpanded ? mrtRowExpandColor(theme) : 'transparent',
                  border: '2px solid',
                  borderColor: allExpanded ? mrtRowExpandColor(theme) : 'transparent',
                  transition: 'all 0.25s ease',
                })}
              >
                <KeyboardDoubleArrowDown
                  sx={(theme) => ({
                    color: allExpanded ? 'white' : theme.palette.primary.main,
                    transform: allExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.25s ease',
                  })}
                />
              </Box>
            </Box>
          )
        },
        Cell: ({ row }) => {
          if (!row || !enableExpanding) return undefined

          // Verificamos si MRT determinó que esta fila NO se puede expandir
          if (!row.getCanExpand()) {
            // Retornamos un contenedor vacío para mantener la estructura y alineación de la tabla
            return <Box sx={{ width: '100%', height: '100%' }} />
          }

          return (
            <Box
              onClick={() => row.toggleExpanded()}
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: alphaNormal(theme.palette.primary.main, 0.1),
                },
                '&:active': {
                  backgroundColor: alphaNormal(theme.palette.primary.main, 0.16),
                },
              })}
            >
              <Box
                className="expand-icon-wrapper"
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: row.getIsExpanded()
                    ? mrtRowExpandColor(theme, { dark: 0.15, light: 0.15 })
                    : 'transparent',
                  border: '1px solid',
                  borderColor: row.getIsExpanded() ? 'primary.main' : 'transparent',
                  transition: 'all 0.25s ease',
                })}
              >
                <KeyboardArrowDown
                  sx={{
                    color: 'primary.main',
                    transform: row.getIsExpanded() ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.25s ease',
                  }}
                />
              </Box>
            </Box>
          )
        },
      },
      'mrt-row-actions': {
        header: 'Opciones',
        size: actionColumnSize,
        minSize: 40,
        muiTableHeadCellProps: {
          sx: {
            '& > .Mui-TableHeadCell-Content': { justifyContent: 'center', width: '100%' },
            textAlign: 'center',
            fontWeight: 700,
          },
        },
        muiTableBodyCellProps: {
          sx: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
        },
      },
    }),
    [enableExpanding, actionColumnSize],
  )

  // Toolbar superior memorizada: referencia estable + no se recalcula si nada relevante cambió.
  const renderTopToolbarCustomActions = useMemo(() => {
    if (!config.title && !config.showIconRefetch && !config.renderTopToolbarCustomActions) {
      return undefined
    }
    return ({ table }: { table: MRT_TableInstance<T> }) => (
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        {(config.showIconRefetch || config.title) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {config.showIconRefetch && (
              <Tooltip title="Actualizar" disableInteractive placement={'top'}>
                <span>
                  <IconButton onClick={() => refetch()} disabled={isFetching} size="small" color={'default'}>
                    <Refresh sx={{ animation: isFetching ? 'spin 1s linear infinite' : 'none' }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {config.title && (
              <Typography variant="subtitle1" fontWeight={700}>
                {config.title}
              </Typography>
            )}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          </Box>
        )}
        {config.renderTopToolbarCustomActions?.({
          table,
          data: tableData,
          refetch,
        })}
      </Box>
    )
  }, [
    config.title,
    config.showIconRefetch,
    config.renderTopToolbarCustomActions,
    refetch,
    isFetching,
    tableData,
  ])

  // Toolbar inferior memorizada, misma razón que arriba.
  const renderBottomToolbarCustomActions = useMemo(() => {
    if (!config.renderBottomToolbarCustomActions) return undefined
    return ({ table }: { table: MRT_TableInstance<T> }) => (
      <Box sx={{ display: 'flex', gap: 1, p: 1 }}>
        {config.renderBottomToolbarCustomActions!({
          table,
          data: tableData,
          refetch,
        })}
      </Box>
    )
  }, [config.renderBottomToolbarCustomActions, tableData, refetch])

  const table = useMaterialReactTable<T>({
    columns: config.columns,
    data: tableData,
    rowCount: totalCount ?? 0,
    // Modos de Control
    manualPagination: isManual,
    manualFiltering: isManual,
    manualSorting: isManual,
    // Estados
    initialState: {
      showColumnFilters: true,
      density: 'compact',
      ...config.additionalOptions?.initialState,
    },
    state: {
      isLoading,
      showProgressBars: isFetching,
      showAlertBanner: isError,
      density: 'compact',
      showColumnFilters: true,
      ...state,
    },
    // Eventos
    ...(hasStateHandlers && {
      onPaginationChange: onStateChange?.onPaginationChange,
      onSortingChange: onStateChange?.onSortingChange,
      onColumnFiltersChange: onStateChange?.onColumnFiltersChange,
      onGlobalFilterChange: onStateChange?.onGlobalFilterChange,
    }),
    ...(onStateChange?.onRowSelectionChange && {
      onRowSelectionChange: onStateChange.onRowSelectionChange,
    }),
    // Ui generales
    enableRowActions,
    enableColumnResizing: true, // habilita el redimensionamiento de columnas
    enableDensityToggle: false, // Habilita el toggle de compacto y normal
    enableMultiRowSelection: config.multiSelection ?? false,
    enableRowSelection: config.enableSelection ?? false,
    enableColumnFilters: true, // Habilita la opción de filtros de columnas
    enableGlobalFilter: false, // Habilita el filtro global, default false
    enableSorting: true, // Habilita el ordenamiento por columna
    // Condicionar explícitamente la habilidad de expandir a la existencia del renderDetailPanel
    // y a que NO estemos en modo móvil (la vista móvil dibuja su propio detalle).
    enableExpanding,
    defaultColumn: { enableSorting: false }, // Pone en false el ordenamiento por defecto
    enableColumnActions: false, // Habilita las acciones de columna, 3 puntitos
    positionToolbarAlertBanner: 'bottom',
    localization: MRT_Localization_ES,
    layoutMode: isFullWidth ? 'grid' : 'semantic',
    // Estilos de Paper y tabla
    muiTablePaperProps: {
      variant: 'outlined',
      sx: { borderRadius: '12px', border: '1px solid', borderColor: 'divider' },
    },
    muiTableProps: {
      sx: { tableLayout: isFullWidth ? 'fixed' : 'auto', width: '100%' },
    },
    displayColumnDefOptions,
    renderRowActions: enableRowActions ? renderRowActions : undefined,
    // Le decimos a MRT qué filas específicas pueden expandirse
    getRowCanExpand: enableExpanding ? getRowCanExpand : undefined,
    renderDetailPanel: enableExpanding ? renderDetailPanel : undefined,
    muiDetailPanelProps: () => ({
      sx: (theme) => ({
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
      }),
    }),
    // Toolbars
    ...(renderTopToolbarCustomActions && { renderTopToolbarCustomActions }),
    ...(renderBottomToolbarCustomActions && { renderBottomToolbarCustomActions }),
    muiToolbarAlertBannerProps: MuiToolbarAlertBannerProps(isError),
    muiFilterTextFieldProps: {
      sx: { m: '0.1rem 0', backgroundColor: (theme) => theme.palette.background.default, width: '80%' },
      variant: 'outlined',
      size: 'small',
      placeholder: 'Filtrar...',
    },
    muiPaginationProps: {
      rowsPerPageOptions: [10, 20, 50],
      showFirstButton: true,
      showLastButton: true,
    },
    muiTableBodyCellProps: { sx: { display: 'flex', alignItems: 'center' } },
    muiTableBodyRowProps: ({ row }) => ({
      ...(config.getRowProps?.(row.original) ?? {}),
    }),
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.paper,
      draggingBorderColor: theme.palette.secondary.main,
      matchHighlightColor: alpha(theme.palette.primary.main, 0.5),
    }),
    ...cleanAdditionalOptions,
  })

  // 4. MODO MÓVIL ADAPTATIVO (TARJETAS PAPER)
  if (isMobileMode) {
    return (
      <MrtMobileListView
        config={config}
        tableData={tableData}
        totalCount={totalCount}
        isLoading={isLoading}
        isFetching={isFetching}
        refetch={refetch}
        renderRowActions={renderRowActionsContent}
        table={table}
        // state={state}
        // onStateChange={onStateChange}
      />
    )
  }

  // 5. MODO PLANO
  if (config.isFlat) {
    return <MrtFlatTable data={tableData} columns={config.columns} options={config.flatOptions} />
  }

  return <MaterialReactTable table={table} />
}

// Tipo auxiliar local solo para tipar el objeto memorizado de displayColumnDefOptions
// sin importar tipos internos no exportados por material-react-table.
type MrtTableConfigDisplayColumnDefOptions<T extends Record<string, any>> = Partial<
  Record<'mrt-row-expand' | 'mrt-row-actions', Partial<MRT_ColumnDef<T, any>>>
>
